// Ported verbatim from the admin app (ai2aim-ems 3bb83bc, "redesign the post
// detail, replies, vote control and dashboard widget").
//
// Verbatim on purpose: every field it reads -- subforumId, title, content,
// author, upvotes, commentCount, createdAt, postCount -- is identical in this
// repo's forum types, so adapting it would only create a second version of
// rules that are meant to agree. A thread that is hot in the workspace has to
// be hot on the public site, and an excerpt has to be cut the same way in both.
//
// Keep it in sync by re-copying rather than by editing here.
//
// The list-row helpers below (threadTags, threadExcerpt, forumSearchTerm,
// searchesOnServer, matchesSearch) follow the portal's change for T100526003
// (employee-management-system#2313), and every helper that reads a list row
// takes `PostListItem`, because list rows are losing `content`.
import type { PostListItem, Subforum } from "./types";

/** "[Ai2aim] Customer Stories" -> "Customer Stories". The bracket prefix is a
 *  seeding artifact, not part of the name a reader needs. Display only — the
 *  stored name is untouched. */
export function displaySpaceName(name: string): string {
  return name.replace(/^\[[^\]]*\]\s*/, "").trim() || name;
}

/** "[Seed] Engineering" -> "EN"; "seed_alice" -> "SA". Never more than two glyphs. */
export function initialsOf(name: string): string {
  const cleaned = name.replace(/^\[[^\]]*\]\s*/, "").trim();
  if (!cleaned) return "?";
  const words = cleaned.split(/[\s._-]+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return cleaned.slice(0, 2).toUpperCase();
}

/**
 * Hashtags authored into the body. There is no tag column — the composer
 * appends them to the content, so this is where a post's topics actually live.
 *
 * The API sends them as `tags` (ai2aim-ems-api#2889). An older build sends only
 * the body, so they are read from it by the same rule; a list row that carries
 * neither has none.
 */
export function threadTags(post: PostListItem): string[] {
  if (Array.isArray(post.tags)) return post.tags;
  if (typeof post.content !== "string") return [];
  const matches = post.content.match(/#[\w-]+/g) ?? [];
  return [...new Set(matches)].slice(0, 5);
}

/**
 * A row's one-line summary: the API's `excerpt`, else one worked out from the
 * body on an older API build, else nothing.
 */
export function threadExcerpt(post: PostListItem): string {
  if (typeof post.excerpt === "string") return post.excerpt;
  return typeof post.content === "string" ? excerptOf(post.content) : "";
}

/** The longest `search` the list routes accept; a longer one is a 400. */
export const FORUM_SEARCH_MAX_LENGTH = 100;

/** A search box's text as the list routes take it: trimmed and capped, `''` for none. */
export function forumSearchTerm(input: string): string {
  return input.trim().slice(0, FORUM_SEARCH_MAX_LENGTH);
}

/**
 * Whether these rows came from an API that searches on the server. `search`
 * and `excerpt` shipped together (ai2aim-ems-api#2889), and a build without
 * them ignores `search` and answers the unfiltered page, so only a row that
 * carries `excerpt` shows that sending `search` filters anything.
 */
export function searchesOnServer(posts: PostListItem[]): boolean {
  return posts.some((p) => typeof p.excerpt === "string");
}

/**
 * The search over loaded rows, for an API that cannot search: title, author,
 * and the body while list rows still carry it (the excerpt once they do not).
 * `q` is already lower-cased.
 */
export function matchesSearch(post: PostListItem, q: string): boolean {
  const body = typeof post.content === "string" ? post.content : (post.excerpt ?? "");
  return (
    post.title.toLowerCase().includes(q) ||
    post.author.toLowerCase().includes(q) ||
    body.toLowerCase().includes(q)
  );
}

/** Body stripped of markdown noise and of the trailing hashtag block, which is
 *  rendered as its own chips rather than repeated in the excerpt. */
export function excerptOf(content: string): string {
  return content
    .replace(/```[\s\S]*?```/g, " ")
    // Headings and list bullets are the only markers where a leading `#` or `-`
    // is syntax. Stripping every hyphen instead turned "org-scoped" into
    // "org scoped", quietly rewriting the author's words.
    .replace(/^\s{0,3}#{1,6}\s+/gm, " ")
    .replace(/^\s*[-*+]\s+/gm, " ")
    .replace(/#[\w-]+/g, " ")
    .replace(/[*_>`~]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 180);
}

/**
 * "Hot" is upvotes alone, at the threshold the design sets. Every surface —
 * index, space page, archive, dashboard widget — reads from here, so a thread
 * cannot be hot in one place and cold in the next.
 */
export const HOT_UPVOTE_THRESHOLD = 20;

export function isHotThread(post: PostListItem): boolean {
  return post.upvotes >= HOT_UPVOTE_THRESHOLD;
}

export function isUnanswered(post: PostListItem): boolean {
  return post.commentCount === 0;
}

/** Posts opened in the last 7 days — the space cards' "new this week". */
export function isNewThisWeek(post: PostListItem): boolean {
  const opened = new Date(post.createdAt).getTime();
  if (Number.isNaN(opened)) return false;
  return Date.now() - opened <= 7 * 24 * 60 * 60 * 1000;
}

export interface SpaceSummary {
  space: Subforum;
  /** Authoritative: the space's own count, not a tally of the loaded page. */
  threads: number;
  /** Derived from loaded posts — only meaningful when the load is complete. */
  replies: number;
  newThisWeek: number;
  lastActivity?: string;
}

/**
 * Per-space aggregates.
 *
 * `threads` comes from the space's own `postCount` so it is right regardless of
 * paging. Everything else is a tally of `posts`, which is one page — read them
 * only when that page is the whole forum (see `isCompleteLoad`).
 */
export function summariseSpaces(spaces: Subforum[], posts: PostListItem[]): SpaceSummary[] {
  return spaces.map((space) => {
    const mine = posts.filter((p) => p.subforumId === space.id);
    const newest = mine.reduce<string | undefined>((latest, p) => {
      if (!latest) return p.createdAt;
      return new Date(p.createdAt) > new Date(latest) ? p.createdAt : latest;
    }, undefined);
    return {
      space,
      threads: space.postCount,
      replies: mine.reduce((n, p) => n + p.commentCount, 0),
      newThisWeek: mine.filter(isNewThisWeek).length,
      lastActivity: newest,
    };
  });
}

/**
 * Whether the loaded page covers every thread the server has.
 *
 * The derived cards — contributor tallies, per-space reply counts, "new this
 * week" — are only true statements about the forum when this holds. When it
 * does not, those cards are omitted rather than shown as a partial tally
 * wearing a global label.
 */
export function isCompleteLoad(loaded: number, total: number | undefined): boolean {
  return typeof total === "number" ? loaded >= total : false;
}

export interface Contributor {
  author: string;
  posts: number;
}

/** Post counts per author across the given posts, most first. */
export function topContributors(posts: PostListItem[], limit = 4): Contributor[] {
  const tally = new Map<string, number>();
  for (const p of posts) tally.set(p.author, (tally.get(p.author) ?? 0) + 1);
  return [...tally.entries()]
    .map(([author, count]) => ({ author, posts: count }))
    .sort((a, b) => b.posts - a.posts || a.author.localeCompare(b.author))
    .slice(0, limit);
}
